import EjectIcon from '@mui/icons-material/Eject';
import {
  Grid,
  Tooltip,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextAsLink,
  detailedListColumn1,
  detailedListColumn2,
  detailedListColumn3,
  detailedListHeader1,
  detailedListHeader2,
  detailedListHeader3,
  nameDiv,
  pointerCursor,
  rotateIcon,
} from '../../styles';

export default function ListTable(props) {
  const {
    rows,
    navigateToTrackingPage,
    orderHandler,
    OrderSortBy,
    isDetailedListPage = false,
    showLeftPanel = true,
  } = props;
  const { t } = useTranslation();

  const listPage = [
    { id: 'ID', label: t('ID') },
    { id: 'Status', label: t('Status') },
    { id: 'Title', label: t('Title') },
    { id: 'CreatedDateSort', label: t('Creation_Date') },
    { id: 'UserName', label: t('Site_Creator') },
    { id: 'Description', label: t('Description') },
    { id: 'ProductFamilyID', label: t('Product_Family') },
    { id: 'SitesImpacted', label: t('Sites_Impacted') },
  ];

  const DetailedlistPage = [
    { id: 'ID', label: t('ID') },
    { id: 'Status', label: t('Status') },
    { id: 'Title', label: t('Title') },
    { id: 'CreatedDateSort', label: t('Creation_Date') },
    { id: 'UserName', label: t('Site_Creator') },
    { id: 'Description', label: t('Description') },
    { id: 'ProductFamilyID', label: t('Product_Family') },
    { id: 'SitesImpacted', label: t('Plants_Impacted') },
    { id: 'Actor', label: t('Actors') },
    { id: 'OwnerName', label: t('Owners') },
    { id: 'LastModified', label: t('Last_Updated') },
    { id: 'Value', label: t('Value') },
    { id: 'Effort', label: t('Effort') },
  ];

  const columns = isDetailedListPage ? DetailedlistPage : listPage;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [columnOrderData, setColumnOrderData] = useState({
    columnId: 'ID',
    order: 'desc',
  });

  useEffect(() => {
    if (
      OrderSortBy &&
      OrderSortBy.columnId !== '' &&
      OrderSortBy.order !== ''
    ) {
      setColumnOrderData({
        columnId: OrderSortBy.columnId,
        order: OrderSortBy.order,
      });
    }
  }, [OrderSortBy]);

  //To handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  //To handle the change of rows per page
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //to set the page index as 0 whenever rows(list) is changing
  useEffect(() => {
    setPage(0);
  }, [rows]);

  //To handle the Sorting
  const handleOrderChange = (columnId, order) => {
    setColumnOrderData({
      columnId: columnId,
      order: order,
    });
    orderHandler(columnId, order);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#1F305E',
      color: theme.palette.common.white,
      fontWeight: 'bold',
      fontSize: '17px',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: '17px',
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.hover,
    },
  }));
  function getSiteStatus(siteStatus, siteName) {
    if (siteStatus !== '') {
      return (
        <div className="text-left">
          <span className="font-bold"> {siteName}: </span>
          <span>{siteStatus}</span>
        </div>
      );
    }
    return null;
  }

  function getSitesImpacted(sitesImpacted) {
    const sites = sitesImpacted ? sitesImpacted.split(',') : [];
    return (
      <ul className="block">
        {sites.map((site, index) => (
          <li key={index}>{site}</li>
        ))}
      </ul>
    );
  }

  return rows.length > 0 ? (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer
        sx={{
          maxHeight: 600,
          maxWidth: isDetailedListPage && showLeftPanel ? '1520px' : '1840px',
        }}
      >
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <StyledTableCell
                  key={column.id}
                  align="center"
                  sx={
                    column.id === 'ID'
                      ? detailedListHeader1
                      : column.id === 'Status'
                        ? detailedListHeader2
                        : column.id === 'Title'
                          ? detailedListHeader3
                          : ''
                  }
                >
                  <Grid style={nameDiv}>
                    <Grid>{column.label}</Grid>
                    <Grid>
                      {column.id === columnOrderData.columnId ? (
                        columnOrderData.order === 'desc' ? (
                          <EjectIcon
                            style={pointerCursor}
                            onClick={() => handleOrderChange(column.id, 'asc')}
                          />
                        ) : (
                          <EjectIcon
                            style={rotateIcon}
                            onClick={() => handleOrderChange(column.id, 'desc')}
                          />
                        )
                      ) : (
                        <Grid
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <EjectIcon
                            style={pointerCursor}
                            onClick={() => handleOrderChange(column.id, 'desc')}
                            fontSize="30px"
                          />
                          <EjectIcon
                            style={rotateIcon}
                            onClick={() => handleOrderChange(column.id, 'asc')}
                            fontSize="30px"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <StyledTableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                  >
                    <StyledTableCell align="center" sx={detailedListColumn1}>
                      <Typography width={50} sx={{ cursor: 'default' }}>
                        {row.ID}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={detailedListColumn2}>
                      {/* <Typography width={150} sx={{ cursor: 'default' }}> */}
                      {props.isMyPecrList ? (
                        <div className="w-[150px]">{row.Status}</div>
                      ) : (
                        <div className="w-[150px]">
                          {/* {row.Status} */}
                          {getSiteStatus(row.CulozStatus, 'Culoz')}
                          {getSiteStatus(row.CharlotteStatus, 'Charlotte')}
                          {getSiteStatus(row.ShanghaiStatus, 'Shanghai')}
                          {getSiteStatus(row.MontillaStatus, 'Montilla')}
                          {getSiteStatus(row.MontluelStatus, 'Montluel')}
                          {getSiteStatus(row.HyderabadStatus, 'Hyderabad')}
                        </div>
                      )}
                      {/* </Typography> */}
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={detailedListColumn3}>
                      <Grid
                        style={TextAsLink}
                        onClick={() => navigateToTrackingPage(row.ID)}
                      >
                        <Tooltip
                          title={row.Title}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                fontSize: '14px',
                              },
                            },
                          }}
                        >
                          <Typography noWrap width={200}>
                            {row.Title}
                          </Typography>
                        </Tooltip>
                      </Grid>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography
                        key={index}
                        width={80}
                        sx={{ cursor: 'default' }}
                      >
                        {row.CreatedDate}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography
                        key={index}
                        width={150}
                        sx={{ cursor: 'default' }}
                      >
                        {row.UserName}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Tooltip
                        title={row.Description}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: '14px',
                            },
                          },
                        }}
                      >
                        <Typography
                          noWrap
                          width={300}
                          sx={{ cursor: 'default' }}
                        >
                          {row.Description}
                        </Typography>
                      </Tooltip>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography width={180} sx={{ cursor: 'default' }}>
                        {row.ProductFamilyID}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <div className="w-[100px]">
                        {getSitesImpacted(row.SitesImpacted)}
                      </div>
                    </StyledTableCell>
                    {isDetailedListPage ? (
                      <>
                        <StyledTableCell align="center">
                          <Typography
                            key={index}
                            width={250}
                            sx={{ cursor: 'default' }}
                          >
                            {row.Actor}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Typography
                            key={index}
                            width={250}
                            sx={{ cursor: 'default' }}
                          >
                            {row.OwnerName}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Typography
                            key={index}
                            width={200}
                            sx={{ cursor: 'default' }}
                          >
                            {row.LastModified}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Typography
                            key={index}
                            width={130}
                            sx={{ cursor: 'default' }}
                          >
                            {row.Value}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Typography
                            key={index}
                            width={120}
                            sx={{ cursor: 'default' }}
                          >
                            {row.Effort}
                          </Typography>
                        </StyledTableCell>
                      </>
                    ) : null}
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={`${t('Rows_per_page')}:`}
        sx={{ boxShadow: 'inset 0 0 2px #000000' }}
      />
    </Paper>
  ) : null;
}
